import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Copy, Trash2 } from "lucide-react";
import { AppShell } from "../layouts/AppShell";
import { Button } from "../primitives/Button";
import { Tabs } from "../primitives/Tabs";
import { hasPermissionInCurrentOrg } from "../../state/orgContext";
import {
  createBookingLink,
  deleteBookingLink,
  getMyBookingRules,
  getTeamAvailabilityPreview,
  listBookingLinks,
  listMyAppointments,
  listOrgMembersForBooking,
  listTeamAppointments,
  putMyBookingRules,
  regenerateBookingLinkToken,
  type BookingLinkItem,
  type OrgMemberBrief,
} from "../../api/bookings";
import { BookingAvailabilityCalendar } from "./bookings/BookingAvailabilityCalendar";
import type { RuleDraft } from "./bookings/BookingAvailabilityCalendar";

function formatAppt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

const commercialModules = [
  { label: "Commercial", sectionHeader: true },
  { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
  { label: "Envoi de courriel", onClick: () => (window.location.hash = "#/email-send") },
  { label: "Rendez-vous", onClick: () => (window.location.hash = "#/bookings") },
  { label: "Clients", onClick: () => (window.location.hash = "#/clients") },
  { label: "Ambassadeurs", onClick: () => (window.location.hash = "#/ambassadors") },
  { label: "Pilotes", onClick: () => (window.location.hash = "#/pilots") },
];

type Tab = "rules" | "links" | "mine" | "team";

export function BookingsPage({ userName = "Utilisateur", currentUserId }: { userName?: string; currentUserId: string }) {
  const canRead = hasPermissionInCurrentOrg("commercial.bookings.read");
  const canTeam = hasPermissionInCurrentOrg("commercial.bookings.team");

  const [tab, setTab] = useState<Tab>("rules");
  const [rules, setRules] = useState<RuleDraft[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  const [members, setMembers] = useState<OrgMemberBrief[]>([]);
  const [links, setLinks] = useState<BookingLinkItem[]>([]);
  const [linkTitle, setLinkTitle] = useState("");
  const [selectedHosts, setSelectedHosts] = useState<Set<string>>(new Set());
  const [lastCreatedUrl, setLastCreatedUrl] = useState<string | null>(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksBusy, setLinksBusy] = useState(false);
  const [linksError, setLinksError] = useState<string | null>(null);

  const [myAppts, setMyAppts] = useState<Awaited<ReturnType<typeof listMyAppointments>>["items"]>([]);
  const [teamAppts, setTeamAppts] = useState<Awaited<ReturnType<typeof listTeamAppointments>>["items"]>([]);
  const [teamPreview, setTeamPreview] = useState<Awaited<ReturnType<typeof getTeamAvailabilityPreview>> | null>(null);
  const [listLoading, setListLoading] = useState(false);

  const loadRules = useCallback(async () => {
    if (!canRead) return;
    setRulesLoading(true);
    setRulesError(null);
    try {
      const { rules: raw } = await getMyBookingRules();
      const mapped: RuleDraft[] = (raw ?? []).map((r) => ({
        weekday: r.weekday,
        specific_date: r.specific_date ?? undefined,
        start_time: r.start_time,
        end_time: r.end_time,
        slot_duration_minutes: r.slot_duration_minutes ?? 30,
        timezone: r.timezone ?? "America/Montreal",
      }));
      setRules(mapped.length ? mapped : []);
    } catch (e: unknown) {
      setRulesError(e instanceof Error ? e.message : "Erreur de chargement");
    } finally {
      setRulesLoading(false);
    }
  }, [canRead]);

  const loadLinksAndMembers = useCallback(async () => {
    if (!canRead) return;
    setLinksLoading(true);
    setLinksError(null);
    try {
      const [m, l] = await Promise.all([listOrgMembersForBooking(), listBookingLinks()]);
      setMembers(m.items ?? []);
      setLinks(l.items ?? []);
      setSelectedHosts((prev) => {
        const next = new Set(prev);
        if (next.size === 0 && currentUserId) next.add(currentUserId);
        return next;
      });
    } catch (e: unknown) {
      setLinksError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLinksLoading(false);
    }
  }, [canRead, currentUserId]);

  const loadTeam = useCallback(async () => {
    if (!canRead || !canTeam) return;
    setListLoading(true);
    try {
      const [a, p] = await Promise.all([listTeamAppointments(), getTeamAvailabilityPreview()]);
      setTeamAppts(a.items ?? []);
      setTeamPreview(p);
    } catch {
      setTeamAppts([]);
      setTeamPreview(null);
    } finally {
      setListLoading(false);
    }
  }, [canRead, canTeam]);

  const loadMine = useCallback(async () => {
    if (!canRead) return;
    setListLoading(true);
    try {
      const a = await listMyAppointments();
      setMyAppts(a.items ?? []);
    } catch {
      setMyAppts([]);
    } finally {
      setListLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  useEffect(() => {
    if (tab === "links") void loadLinksAndMembers();
  }, [tab, loadLinksAndMembers]);

  useEffect(() => {
    if (tab === "mine") void loadMine();
  }, [tab, loadMine]);

  useEffect(() => {
    if (tab === "team") void loadTeam();
  }, [tab, loadTeam]);

  const saveRules = async () => {
    setRulesSaving(true);
    setRulesError(null);
    try {
      await putMyBookingRules(rules);
    } catch (e: unknown) {
      setRulesError(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setRulesSaving(false);
    }
  };

  const toggleHost = (id: string) => {
    setSelectedHosts((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const createLink = async () => {
    const ids = [...selectedHosts];
    if (ids.length === 0) {
      setLinksError("Sélectionnez au moins une personne pour la réservation.");
      return;
    }
    setLinksBusy(true);
    setLinksError(null);
    try {
      const res = await createBookingLink({
        title: linkTitle.trim() || null,
        allowed_host_user_ids: ids,
      });
      setLastCreatedUrl(res.url);
      await loadLinksAndMembers();
    } catch (e: unknown) {
      setLinksError(e instanceof Error ? e.message : "Création impossible");
    } finally {
      setLinksBusy(false);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      window.prompt("Copier le lien :", text);
    }
  };

  const removeLink = async (id: string) => {
    if (!window.confirm("Supprimer ce lien ? Les clients ne pourront plus l’utiliser.")) return;
    setLinksBusy(true);
    try {
      await deleteBookingLink(id);
      await loadLinksAndMembers();
    } catch (e: unknown) {
      setLinksError(e instanceof Error ? e.message : "Suppression impossible");
    } finally {
      setLinksBusy(false);
    }
  };

  const regenerateLink = async (id: string) => {
    if (
      !window.confirm(
        "Un nouveau jeton va être créé. L’ancienne adresse de réservation ne fonctionnera plus. Continuer ?"
      )
    ) {
      return;
    }
    setLinksBusy(true);
    setLinksError(null);
    try {
      const res = await regenerateBookingLinkToken(id);
      await copyText(res.url);
      await loadLinksAndMembers();
    } catch (e: unknown) {
      setLinksError(e instanceof Error ? e.message : "Régénération impossible");
    } finally {
      setLinksBusy(false);
    }
  };

  const memberName = useMemo(() => {
    const m = new Map<string, string>();
    members.forEach((x) => m.set(x.id, x.display_name));
    return m;
  }, [members]);

  if (!canRead) {
    return (
      <AppShell
        topbar={<div className="text-sm text-muted">Rendez-vous</div>}
        userName={userName}
        modules={commercialModules}
        userMenuItems={[
          { label: "Profil", onClick: () => (window.location.hash = "#/profile") },
          { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        ]}
      >
        <div className="p-6 max-w-lg text-sm text-muted">
          Vous n’avez pas accès au module Rendez-vous pour cette organisation. Demandez à un administrateur la permission
          « commercial.bookings.read ».
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Rendez-vous</div>}
      userName={userName}
      modules={commercialModules}
      userMenuItems={[
        { label: "Profil", onClick: () => (window.location.hash = "#/profile") },
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
      ]}
    >
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface">
            <CalendarClock size={20} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-section text-text">Rendez-vous</h1>
            <p className="text-sm text-muted">
              Définissez vos disponibilités, créez un lien de réservation (vous pouvez y inclure des collègues), et consultez
              vos rendez-vous confirmés.
            </p>
          </div>
        </div>

        <Tabs
          variant="underline"
          activeId={tab}
          onChange={(id) => setTab(id as Tab)}
          tabs={[
            { id: "rules", label: "Disponibilités" },
            { id: "links", label: "Liens publics" },
            { id: "mine", label: "Mes rendez-vous" },
            ...(canTeam ? [{ id: "team", label: "Équipe" }] : []),
          ]}
        />

        {tab === "rules" && (
          <section className="space-y-4 max-w-5xl">
            {rulesError && <p className="text-sm text-red-600">{rulesError}</p>}
            <BookingAvailabilityCalendar
              rules={rules}
              onRulesChange={setRules}
              loading={rulesLoading}
            />
            {!rulesLoading && (
              <div className="pt-2">
                <Button
                  label={rulesSaving ? "Enregistrement…" : "Enregistrer sur le serveur"}
                  intent="primary"
                  size="md"
                  onClick={() => void saveRules()}
                  state={rulesSaving ? "disabled" : "default"}
                />
                <p className="text-xs text-muted mt-2">
                  Les plages s’appliquent chaque semaine au jour choisi. Cliquez de nouveau sur un jour du calendrier pour
                  modifier ou ajouter des créneaux.
                </p>
              </div>
            )}
          </section>
        )}

        {tab === "links" && (
          <section className="space-y-4">
            <p className="text-sm text-muted">
              Cochez les personnes avec lesquelles un client peut prendre rendez-vous via ce lien. L’URL est copiable depuis
              la liste des liens créés (les liens très anciens sans jeton enregistré peuvent être régénérés — l’ancienne URL
              cessera alors de fonctionner).
            </p>
            {linksError && <p className="text-sm text-red-600">{linksError}</p>}
            {lastCreatedUrl && (
              <div className="rounded-md border border-border bg-surface p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm break-all flex-1">{lastCreatedUrl}</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-medium text-accent"
                  onClick={() => void copyText(lastCreatedUrl)}
                >
                  <Copy size={14} /> Copier
                </button>
              </div>
            )}
            {linksLoading ? (
              <p className="text-sm text-muted">Chargement…</p>
            ) : (
              <>
                <label className="flex flex-col gap-1 text-sm max-w-md">
                  <span className="text-muted">Titre du lien (optionnel)</span>
                  <input
                    className="rounded border border-border px-3 py-2 bg-background"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e.target.value)}
                    placeholder="Ex. Démonstration produit"
                  />
                </label>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Hôtes réservables</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {members.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedHosts.has(m.id)}
                          onChange={() => toggleHost(m.id)}
                        />
                        <span>{m.display_name}</span>
                        <span className="text-xs text-muted truncate">{m.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button
                  label={linksBusy ? "Création…" : "Créer le lien et afficher l’URL"}
                  intent="primary"
                  size="md"
                  onClick={() => void createLink()}
                  state={linksBusy ? "disabled" : "default"}
                />

                <div className="mt-6">
                  <h3 className="text-sm font-semibold mb-2">Liens créés</h3>
                  {!links.length ? (
                    <p className="text-sm text-muted">Aucun lien pour l’instant.</p>
                  ) : (
                    <ul className="divide-y divide-border border border-border rounded-md">
                      {links.map((l) => (
                        <li key={l.id} className="p-3 flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium">{l.title || "Sans titre"}</div>
                            <div className="text-xs text-muted mt-1">
                              Hôtes :{" "}
                              {(l.allowed_host_user_ids ?? [])
                                .map((id) => memberName.get(String(id)) ?? String(id))
                                .join(", ")}
                            </div>
                            <div className="text-xs text-muted">
                              Créé le {l.created_at ? formatAppt(l.created_at) : "—"}
                            </div>
                            {l.url ? (
                              <div className="mt-2 text-xs font-mono break-all text-muted border border-border/80 rounded px-2 py-1 bg-background/50">
                                {l.url}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            {l.url ? (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm font-medium text-accent"
                                onClick={() => void copyText(l.url!)}
                                disabled={linksBusy}
                              >
                                <Copy size={14} /> Copier le lien
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm font-medium text-warning"
                                title="Crée un nouveau jeton et copie l’URL (l’ancienne cesse de fonctionner)"
                                onClick={() => void regenerateLink(l.id)}
                                disabled={linksBusy}
                              >
                                Régénérer le jeton
                              </button>
                            )}
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-sm text-red-600"
                              onClick={() => void removeLink(l.id)}
                              disabled={linksBusy}
                            >
                              <Trash2 size={14} /> Supprimer
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {tab === "mine" && (
          <section>
            {listLoading ? (
              <p className="text-sm text-muted">Chargement…</p>
            ) : !myAppts.length ? (
              <p className="text-sm text-muted">Aucun rendez-vous à venir.</p>
            ) : (
              <ul className="divide-y divide-border border border-border rounded-md">
                {myAppts.map((a) => (
                  <li key={a.id} className="p-3 text-sm">
                    <div className="font-medium">{a.client_name}</div>
                    <div className="text-muted">{a.client_email}</div>
                    <div className="mt-1">
                      {formatAppt(a.starts_at)} — {formatAppt(a.ends_at)}
                    </div>
                    {a.client_notes ? <div className="text-xs mt-1 text-muted">Note : {a.client_notes}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {tab === "team" && canTeam && (
          <section className="space-y-6">
            <p className="text-sm text-muted">
              Aperçu des disponibilités (nombre de créneaux à venir) et liste des rendez-vous de l’équipe.
            </p>
            {listLoading ? (
              <p className="text-sm text-muted">Chargement…</p>
            ) : (
              <>
                {teamPreview && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Aperçu ({teamPreview.from_date} → {teamPreview.to_date})</h3>
                    <ul className="divide-y divide-border border border-border rounded-md text-sm">
                      {teamPreview.items.map((row) => (
                        <li key={row.user_id} className="p-3 flex justify-between gap-4">
                          <span>{row.display_name}</span>
                          <span className="text-muted">
                            {row.has_rules ? `${row.upcoming_slot_count} créneau(x)` : "Aucune règle"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Tous les rendez-vous (équipe)</h3>
                  {!teamAppts.length ? (
                    <p className="text-sm text-muted">Aucun rendez-vous.</p>
                  ) : (
                    <ul className="divide-y divide-border border border-border rounded-md">
                      {teamAppts.map((a) => (
                        <li key={a.id} className="p-3 text-sm">
                          <div className="font-medium">{a.host_display_name ?? "Hôte"}</div>
                          <div>{a.client_name}</div>
                          <div className="text-muted">{a.client_email}</div>
                          <div className="mt-1">
                            {formatAppt(a.starts_at)} — {formatAppt(a.ends_at)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
